"use client";

import { diatonicChordsForKey, type DiatonicChord } from "@/lib/music/chord-catalog";
import { useChordLabel, useNoteLabel, useNoteLabels } from "@/hooks/use-notation";
import { formatAccidentals, pitchClassToName, QUALITY_INTERVALS, ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import { useActiveDiatonicDegrees } from "@/hooks/use-active-diatonic-degrees";
import {
  BORDER,
  BORDER_HI,
  FONT_MONO,
  SURFACE,
  SURFACE_LO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "@/components/ui/organisms/chord-practice/palette";
import { usePressedPitchClasses } from "@/hooks/use-pressed-pitch-classes";
import {
  DEGREES_BY_FUNCTION,
  DIATONIC_FUNCTION,
  FUNCTION_COLOR,
  FUNCTION_LABEL,
  FUNCTION_NARRATIVE,
  POPULAR_PROGRESSIONS,
} from "./harmony-data";
import { ScaleFrieze } from "./harmony-scale-frieze";
import { LEXICON, Term } from "./harmony-term";
import type { HarmonyFocus, HarmonyTonality } from "./types";

interface HarmonyOverviewProps {
  tonality: HarmonyTonality;
  onFocusChange: (focus: HarmonyFocus) => void;
}

const SCALE_INTERVALS_MAJOR = [0, 2, 4, 5, 7, 9, 11];
const SCALE_INTERVALS_MINOR = [0, 2, 3, 5, 7, 8, 10];

/**
 * Stage central de la tab Harmonie pour le focus «&nbsp;overview&nbsp;» :
 * 3 cards (Gamme · Accords · Progressions) qui dérivent les unes des autres.
 * Le détail par focus arrive en Phase C/D.
 */
export function HarmonyOverview({ tonality, onFocusChange }: HarmonyOverviewProps) {
  const diatonic = diatonicChordsForKey(tonality.tonic, tonality.mode);
  const pressedPcs = usePressedPitchClasses();
  const activeDegrees = useActiveDiatonicDegrees(diatonic);

  return (
    <div
      style={{
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        padding: "32px 40px 48px",
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at 30% 0%, ${VIOLET_SOFT} 0%, transparent 55%)`,
          pointerEvents: "none",
        }}
      />

      <Header tonality={tonality} />

      <ScaleCard tonality={tonality} pressedPcs={pressedPcs} onOpen={() => onFocusChange({ kind: "scale" })} />

      <DerivationArrow
        from="Gamme"
        to="Accords"
        hint={
          <>
            Empilez les notes 1-3-5 sur chaque <Term tip={LEXICON.degree}>degré</Term>
          </>
        }
      />

      <ChordsCard
        diatonic={diatonic}
        activeDegrees={activeDegrees}
        onOpen={(degree) => onFocusChange({ kind: "chord", degree })}
        onOpenAll={() => onFocusChange({ kind: "chord", degree: 0 })}
      />

      <DerivationArrow
        from="Accords"
        to="Progressions"
        hint={
          <>
            Enchaînez les <Term tip={LEXICON.degree}>degrés</Term> pour raconter une histoire harmonique
          </>
        }
      />

      <ProgressionsCard
        diatonic={diatonic}
        activeDegrees={activeDegrees}
        onOpen={(id) => onFocusChange({ kind: "progression", id })}
      />
    </div>
  );
}

function Header({ tonality }: { tonality: HarmonyTonality }) {
  const toNoteLabel = useNoteLabel();
  const tonicLabel = toNoteLabel(tonality.tonic);
  const modeLabel = tonality.mode === "major" ? "majeur" : "mineur";

  return (
    <header
      style={{
        position: "relative",
        zIndex: 1,
        marginBottom: 32,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontFamily: FONT_MONO,
          color: VIOLET_BRIGHT,
          opacity: 0.6,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        Atlas tonal · {tonicLabel} {modeLabel}
      </div>
      <h1
        style={{
          fontSize: 32,
          fontFamily: "Inter, sans-serif",
          fontWeight: 300,
          color: TEXT,
          letterSpacing: "-0.02em",
          margin: 0,
          lineHeight: 1.1,
        }}
      >
        Tout part de{" "}
        <span
          style={{
            fontWeight: 500,
            background: `linear-gradient(135deg, ${VIOLET_BRIGHT}, ${VIOLET})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {tonicLabel} {modeLabel}
        </span>
        .
      </h1>
      <p
        style={{
          fontSize: 14,
          color: TEXT_DIM,
          fontFamily: "Inter, sans-serif",
          margin: "8px 0 0",
          maxWidth: 580,
          lineHeight: 1.5,
        }}
      >
        La gamme donne les <em>notes disponibles</em>. Les accords s&apos;y construisent en empilant des{" "}
        <Term tip={LEXICON.third}>tierces</Term>. Les progressions racontent une histoire en enchaînant ces accords.
      </p>
    </header>
  );
}

function ScaleCard({
  tonality,
  pressedPcs,
  onOpen,
}: {
  tonality: HarmonyTonality;
  pressedPcs: ReadonlySet<number>;
  onOpen: () => void;
}) {
  const intervals = tonality.mode === "major" ? SCALE_INTERVALS_MAJOR : SCALE_INTERVALS_MINOR;

  return (
    <Card tag="01 · La gamme" title="Les notes de la maison" cta="Étudier la gamme" onCta={onOpen} glow>
      <ScaleFrieze
        tonic={tonality.tonic}
        semitones={intervals}
        flavor={tonality.mode}
        density="compact"
        activePitchClasses={pressedPcs}
      />
    </Card>
  );
}

function ChordsCard({
  diatonic,
  activeDegrees,
  onOpen,
  onOpenAll,
}: {
  diatonic: DiatonicChord[];
  activeDegrees: ReadonlySet<number>;
  onOpen: (degree: number) => void;
  onOpenAll: () => void;
}) {
  return (
    <Card tag="02 · Les accords" title="7 accords, 3 fonctions" cta="Travailler les accords" onCta={onOpenAll}>
      <BuilderHint />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 8,
          marginTop: 12,
        }}
      >
        {diatonic.map((dc, i) => (
          <DiatonicChordCell
            key={dc.entry.id}
            chord={dc}
            functionTag={DIATONIC_FUNCTION[i] ?? "T"}
            active={activeDegrees.has(i)}
            onClick={() => onOpen(i)}
          />
        ))}
      </div>
      <FunctionTrio diatonic={diatonic} />
    </Card>
  );
}

function BuilderHint() {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "10px 12px",
        borderRadius: 8,
        background: "rgba(139,92,246,0.05)",
        border: `1px solid ${BORDER}`,
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        color: TEXT_DIM,
        lineHeight: 1.5,
      }}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          color: VIOLET_BRIGHT,
          letterSpacing: "0.16em",
          fontWeight: 600,
          flexShrink: 0,
          alignSelf: "center",
        }}
      >
        1·3·5
      </span>
      <span>
        On empile <strong style={{ color: TEXT }}>une note sur deux</strong> à partir de chaque{" "}
        <Term tip={LEXICON.degree}>degré</Term> (1·3·5), c&apos;est ce qu&apos;on appelle une{" "}
        <Term tip={LEXICON.triad}>triade</Term>. La nature des <Term tip={LEXICON.third}>tierces</Term> rencontrées
        (majeure ou mineure) détermine la qualité de l&apos;accord, c&apos;est pour ça que I est majeur, ii mineur, vii°
        diminué.
      </span>
    </div>
  );
}

function DiatonicChordCell({
  chord,
  functionTag,
  active,
  onClick,
}: {
  chord: DiatonicChord;
  functionTag: string;
  active: boolean;
  onClick: () => void;
}) {
  const chordLabel = useChordLabel();
  const noteLabels = useNoteLabels();

  const fnColor = FUNCTION_COLOR[functionTag] ?? VIOLET_BRIGHT;
  const notes = chordNotes(chord.entry.root, chord.entry.quality);
  const restingBorder = active ? VIOLET_BRIGHT : BORDER;
  const restingBackground = active ? "rgba(139,92,246,0.16)" : SURFACE;
  const hoverBorder = active ? VIOLET_BRIGHT : BORDER_HI;
  const hoverBackground = active ? "rgba(139,92,246,0.22)" : "rgba(139,92,246,0.06)";

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        padding: "12px 6px 10px",
        borderRadius: 8,
        border: `1px solid ${restingBorder}`,
        background: restingBackground,
        cursor: "pointer",
        boxShadow: active ? "0 0 14px rgba(196,181,253,0.35)" : "none",
        transition: "border-color 150ms, background 150ms, box-shadow 150ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = hoverBorder;
        e.currentTarget.style.background = hoverBackground;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = restingBorder;
        e.currentTarget.style.background = restingBackground;
      }}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 11,
          color: fnColor,
          letterSpacing: "0.06em",
          fontWeight: 600,
        }}
      >
        {chord.roman}
      </span>
      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 18,
          color: TEXT,
          fontWeight: 500,
          letterSpacing: "-0.01em",
        }}
      >
        {chordLabel(chord.entry.symbol)}
      </span>
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          color: TEXT_DIM,
          letterSpacing: "0.04em",
          textAlign: "center",
        }}
      >
        {noteLabels(notes).join("·")}
      </span>
      <span
        style={{
          marginTop: 2,
          padding: "1px 6px",
          borderRadius: 3,
          fontFamily: FONT_MONO,
          fontSize: 9,
          color: fnColor,
          background: fnColor.replace("0.85", "0.14"),
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {functionTag}
      </span>
    </button>
  );
}

function chordNotes(root: string, quality: string): string[] {
  const rootPc = ROOT_PITCH_CLASS[root] ?? 0;
  const intervals = QUALITY_INTERVALS[quality] ?? [0, 4, 7];

  return intervals.slice(0, 3).map((iv) => pitchClassToName((rootPc + iv) % 12, root));
}

function FunctionTrio({ diatonic }: { diatonic: DiatonicChord[] }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 9,
          color: TEXT_MUTE,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        Le cycle harmonique
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 6,
        }}
      >
        {(["T", "SD", "D"] as const).flatMap((f, idx) => [
          <FunctionTile key={f} tag={f} diatonic={diatonic} />,
          idx < 2 ? <FunctionArrow key={`arrow-${f}`} /> : null,
        ])}
        <ResolveCurl />
      </div>
      <p
        style={{
          margin: "12px 0 0",
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: TEXT_DIM,
          lineHeight: 1.55,
        }}
      >
        T → SD → D → T : c&apos;est le squelette de presque toutes les progressions classiques. La{" "}
        <Term tip={LEXICON.function}>dominante</Term> contient la <Term tip={LEXICON.sensible}>sensible</Term> (le 7
        <sup>e</sup> <Term tip={LEXICON.degree}>degré</Term>) qui appelle naturellement la{" "}
        <Term tip={LEXICON.tonic}>tonique</Term>, d&apos;où la <Term tip={LEXICON.cadence}>résolution</Term>.
      </p>
    </div>
  );
}

function FunctionTile({ tag, diatonic }: { tag: "T" | "SD" | "D"; diatonic: DiatonicChord[] }) {
  const color = FUNCTION_COLOR[tag];
  const degrees = DEGREES_BY_FUNCTION[tag] ?? [];
  const chordSymbols = degrees.map((d) => diatonic[d - 1]).filter((dc): dc is DiatonicChord => dc !== undefined);

  return (
    <div
      style={{
        flex: 1,
        padding: "12px 14px",
        borderRadius: 10,
        background: color?.replace("0.85", "0.08"),
        border: `1px solid ${color?.replace("0.85", "0.35")}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            background: color,
            color: "#0a0a10",
            fontFamily: FONT_MONO,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.04em",
          }}
        >
          {tag}
        </span>
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: TEXT,
          }}
        >
          {FUNCTION_LABEL[tag]}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        {chordSymbols.map((dc) => (
          <span
            key={dc.entry.id}
            style={{
              padding: "2px 8px",
              borderRadius: 4,
              background: "rgba(0,0,0,0.25)",
              fontFamily: FONT_MONO,
              fontSize: 11,
              color,
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
          >
            {dc.roman}
          </span>
        ))}
      </div>
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 11,
          color: TEXT_DIM,
          lineHeight: 1.4,
        }}
      >
        {FUNCTION_NARRATIVE[tag]}
      </div>
    </div>
  );
}

function FunctionArrow() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M2 7h9M8 4l3 3-3 3"
          stroke={VIOLET_BRIGHT}
          strokeOpacity="0.7"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function ResolveCurl() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
      }}
      title="Résolution vers la tonique"
    >
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path
          d="M3 11a8 8 0 1 1 13.5 5.7"
          stroke={VIOLET_BRIGHT}
          strokeOpacity="0.55"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeDasharray="3 3"
          fill="none"
        />
        <path
          d="M14 18l3-1.5-1-3"
          stroke={VIOLET_BRIGHT}
          strokeOpacity="0.7"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

function ProgressionsCard({
  diatonic,
  activeDegrees,
  onOpen,
}: {
  diatonic: DiatonicChord[];
  activeDegrees: ReadonlySet<number>;
  onOpen: (id: string) => void;
}) {
  const chordLabel = useChordLabel();

  return (
    <Card
      tag="03 · Les progressions"
      title="Quelques voyages classiques"
      cta="Explorer les progressions"
      onCta={() => onOpen(POPULAR_PROGRESSIONS[0]?.id ?? "")}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginTop: 6,
        }}
      >
        {POPULAR_PROGRESSIONS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onOpen(p.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 14px",
              borderRadius: 10,
              border: `1px solid ${BORDER}`,
              background: SURFACE_LO,
              cursor: "pointer",
              textAlign: "left",
              transition: "border-color 150ms, background 150ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = BORDER_HI;
              e.currentTarget.style.background = "rgba(139,92,246,0.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = BORDER;
              e.currentTarget.style.background = SURFACE_LO;
            }}
          >
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: 11,
                color: VIOLET_BRIGHT,
                letterSpacing: "0.08em",
                width: 200,
                flexShrink: 0,
              }}
            >
              {p.numerals}
            </div>
            <div
              style={{
                display: "flex",
                gap: 4,
                flexShrink: 0,
              }}
            >
              {p.degrees.map((d, i) => {
                const dc = diatonic[d];

                if (!dc) {
                  return null;
                }

                const isActive = activeDegrees.has(d);

                return (
                  <span
                    key={i}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 4,
                      background: isActive ? "rgba(139,92,246,0.32)" : "rgba(139,92,246,0.14)",
                      border: `1px solid ${isActive ? VIOLET_BRIGHT : VIOLET_GLOW}`,
                      fontFamily: FONT_MONO,
                      fontSize: 11,
                      color: isActive ? "#fff" : TEXT,
                      boxShadow: isActive ? "0 0 10px rgba(196,181,253,0.4)" : "none",
                      transition: "background 150ms, border-color 150ms, color 150ms, box-shadow 150ms",
                    }}
                  >
                    {chordLabel(dc.entry.symbol)}
                    
                  </span>
                );
              })}
            </div>
            <div
              style={{
                marginLeft: "auto",
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                color: TEXT_DIM,
                lineHeight: 1.3,
              }}
            >
              <div className="text-right" style={{ color: TEXT, fontWeight: 500 }}>
                {p.label}
              </div>
              <div style={{ color: TEXT_MUTE, fontSize: 11 }}>{p.context}</div>
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
}

function Card({
  tag,
  title,
  cta,
  onCta,
  glow,
  children,
}: {
  tag: string;
  title: string;
  cta: string;
  onCta: () => void;
  glow?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        position: "relative",
        zIndex: 1,
        padding: "20px 22px 22px",
        borderRadius: 14,
        border: `1px solid ${glow ? VIOLET_GLOW : BORDER}`,
        background: glow ? "linear-gradient(180deg, rgba(139,92,246,0.06), rgba(255,255,255,0.02))" : SURFACE_LO,
        boxShadow: glow ? "0 0 32px rgba(139,92,246,0.08)" : "0 1px 0 rgba(255,255,255,0.02)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 14,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 9,
              fontFamily: FONT_MONO,
              color: VIOLET_BRIGHT,
              opacity: 0.6,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            {tag}
          </div>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 18,
              fontWeight: 400,
              color: TEXT,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </div>
        </div>
        <button
          type="button"
          onClick={onCta}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            border: `1px solid ${VIOLET_GLOW}`,
            background: "rgba(139,92,246,0.12)",
            color: VIOLET_BRIGHT,
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            transition: "background 150ms, color 150ms",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = VIOLET;
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(139,92,246,0.12)";
            e.currentTarget.style.color = VIOLET_BRIGHT;
          }}
        >
          {cta} →
        </button>
      </div>
      {children}
    </section>
  );
}

function DerivationArrow({ from, to, hint }: { from: string; to: string; hint: React.ReactNode }) {
  return (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        padding: "16px 0 14px 22px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <svg width="18" height="36" viewBox="0 0 18 36" fill="none">
        <line
          x1="9"
          y1="0"
          x2="9"
          y2="28"
          stroke={VIOLET_BRIGHT}
          strokeOpacity="0.5"
          strokeWidth="1.4"
          strokeDasharray="3 3"
        />
        <path
          d="M3 26L9 32L15 26"
          stroke={VIOLET_BRIGHT}
          strokeOpacity="0.6"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      <div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 9,
            color: VIOLET_BRIGHT,
            opacity: 0.55,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {from} → {to}
        </div>
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: TEXT_DIM,
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      </div>
    </div>
  );
}
